<template>
  <v-container fluid>
    <c-crud-filters v-if="withFilters" :is-mobile="$isMobile()">
      <template v-slot:content>
        <v-row>
          <v-col v-if="withCompany" cols="12" xs="6" md="2">
            <c-companies-autocomplete
              v-model="filters.company_id"
              :label="$lang('companies.title')"
              clearable
              dense
            />
          </v-col>
          <v-col cols="12" xs="6" md="2">
            <v-date
              v-model="filters.from"
              :label="$lang('main.words.from')"
              clearable
              dense
            />
          </v-col>
          <v-col cols="12" xs="6" md="2">
            <v-date
              v-model="filters.to"
              :label="$lang('main.words.to')"
              :min="filters.from"
              clearable
              dense
            ></v-date>
          </v-col>
          <v-col cols="12" xs="12" md="2">
            <v-select
              :items="$in('ConstHasOptionFilters')"
              v-model="filters.is_charged"
              :label="$lang('companies.payments.is_charged')"
              clearable
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" xs="12" md="2">
            <v-select
              :items="$in('ConstHasOptionFilters')"
              v-model="filters.is_paid"
              :label="$lang('companies.payments.is_paid')"
              clearable
              dense
            />
          </v-col>
          <v-col cols="12" xs="12" md="2">
            <v-select
              :items="$in('ConstHasOptionFilters')"
              v-model="filters.has_invoice"
              label="Fatturati"
              clearable
              dense
            />
            <v-select
              :items="$in('ConstHasOptionFilters')"
              v-model="filters.invoiceable"
              label="Fatturabile"
              clearable
              dense
            />
          </v-col>
        </v-row>
      </template>
    </c-crud-filters>
    <v-skeleton-loader
      v-show="request.loading"
      :tile="false"
      :boilerplate="false"
      type="actions,table-row-divider@10"
      class="mx-auto"
    ></v-skeleton-loader>
    <v-card flat v-show="!request.loading">
       <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-if="!!options.companyId && $can('EDIT_COMPANIES_SUBSCRIPTIONS')" @click="openCreation" color="primary">
            <v-icon>mdi-plus</v-icon>
            {{ $lang("main.crud.add") }}
          </v-btn>
      </v-card-actions>
      <v-card-text>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th v-if="withCompany" class="text-left">
                  {{ $lang("companies.title") }}
                </th>
                <th class="text-left sortable">
                  <div @click="sort('scheduled_at')">
                    <v-icon v-if="filters.sortBy === 'scheduled_at' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                    <v-icon v-if="filters.sortBy === 'scheduled_at' && isDesc" small color="primary">mdi-sort-descending</v-icon>
                    <v-icon v-if="filters.sortBy !== 'scheduled_at'" small>mdi-sort</v-icon>
                    {{ $lang("companies.payments.scheduled_at") }}
                  </div>
                  <div v-if="$can('INTERNAL_SUPPORT')" @click="sort('charged_at')">
                    <v-icon v-if="filters.sortBy === 'charged_at' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                    <v-icon v-if="filters.sortBy === 'charged_at' && isDesc" small color="primary">mdi-sort-descending</v-icon>
                    <v-icon v-if="filters.sortBy !== 'charged_at'" small>mdi-sort</v-icon>
                    {{ $lang("companies.payments.is_charged") }}
                  </div>
                </th>
                <th class="text-left">
                  {{ $lang("companies.payments.description") }}
                </th>
                <th class="text-left sortable" @click="sort('amount')">
                  <v-icon v-if="filters.sortBy === 'amount' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                  <v-icon v-if="filters.sortBy === 'amount' && isDesc" small color="primary">mdi-sort-descending</v-icon>
                  <v-icon v-if="filters.sortBy !== 'amount'" small>mdi-sort</v-icon>
                  {{ $lang("companies.payments.amount") }}
                </th>
                <th class="text-left sortable" @click="sort('paid_at')">
                  <v-icon v-if="filters.sortBy === 'paid_at' && isAsc" small color="primary">mdi-sort-ascending</v-icon>
                  <v-icon v-if="filters.sortBy === 'paid_at' && isDesc" small color="primary">mdi-sort-descending</v-icon>
                  <v-icon v-if="filters.sortBy !== 'paid_at'" small>mdi-sort</v-icon>
                  {{ $lang("companies.payments.paid_at") }}
                </th>
                <th class="text-right" v-if="$can('EDIT_COMPANIES_SUBSCRIPTIONS')">
                  {{ $lang("main.crud.actions") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in collection" :key="index">
                <td v-if="withCompany" class="text-left">
                  <onboarding-status :item="item.company" />
                  {{ item.company.name ? item.company.name : '' }}
                </td>
                <td class="text-left">
                  <v-icon small>mdi-clock</v-icon> {{ $toDateFormat(item.scheduled_at) }} <br>
                  <span v-if="$can('INTERNAL_SUPPORT')">
                    <v-icon v-if="!item.charged_at" color="error" small>mdi-cancel</v-icon>
                    <v-icon v-if="!!item.charged_at" color="success" small>mdi-cash</v-icon> {{ $toDateFormat(item.charged_at) }}
                  </span>
                </td>
                <td class="text-left">
                  <v-chip v-if="$can('INTERNAL_SUPPORT')" small color="admin" x-small>
                    {{ item.id }}
                  </v-chip>
                  {{ item.description }}
                </td>
                <td class="text-left">
                  {{ $money(item.amount, 'EUR') }}
                </td>
                <td class="text-left">
                  <v-chip v-if="!!item.is_disputed" label small color="error">
                    <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                    {{ $lang('companies.payments.is_disputed') }}
                  </v-chip>
                  {{ $toDateFormat(item.paid_at) }}
                </td>
                <td class="text-right" v-if="$can('EDIT_COMPANIES_SUBSCRIPTIONS')">
                  <v-tooltip bottom> <!-- v-if="!item.charged_at && canCharge(item)" -->
                    <template v-slot:activator="{ on }">
                      <v-btn color="primary" small v-on="on" class="mr-2" @click="charge(item.id)" :loading="chargeRequest.loading && chargeRequest.params.id === item.id">
                        {{ $lang("companies.payments.charge") }}
                      </v-btn>
                    </template>
                    <span>{{ $lang("companies.payments.charge") }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="$can('CREATE_COMPANY_INVOICES') && !!item.invoice_detail" bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn @click="exportInvoiceXML(item.invoice_detail.company_invoice_id)" v-on="on" class="ma-2" fab color="success" x-small>
                        <v-icon small>mdi-export</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $lang("companies.payments.export_invoice") }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="$can('CREATE_COMPANY_INVOICES') && !item.invoice_detail" bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn @click="openInvoiceCreation(item)" v-on="on" class="ma-2" fab color="primary" x-small>
                        <v-icon small>mdi-receipt-text</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $lang("companies.payments.generate_invoice") }}</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="$can('EDIT_COMPANY_INVOICES')">
                    <template v-slot:activator="{ on }">
                      <v-icon @click="openEditing(item.id)" small class="mr-2" v-on="on">mdi-pencil</v-icon>
                    </template>
                    <span>{{ $lang("main.crud.edit") }}</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="$can('DELETE_COMPANY_INVOICES')">
                    <template v-slot:activator="{ on }">
                      <v-icon @click="openRemoving(item.id)" small class="mr-2" v-on="on">mdi-delete</v-icon>
                    </template>
                    <span>{{ $lang("main.crud.remove") }}</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="!collection.length">
                <td colspan="4" class="text-center">
                  {{ $lang("main.crud.no_records") }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-pagination
          v-if="pagination.total > pagination.perPage"
          v-model="pagination.page"
          :length="pagination.totalPages"
          :total-visible="10"
        />

        <v-confirmation
          v-model="isOpenRemove"
          :options="{
            loading: deletionLoading,
            yes: () => remove(activeId)
          }"
        />

        <v-dialog v-model="isOpenCreation" transition="dialog-bottom-transition" width="50vw">
          <creation-form
            v-if="isOpenCreation"
            @cancel="closeCreation"
            @error="failed"
            @success="created"
            :createData="{
              company_subscription_plan_id: options.subscriptionId,
              company_id: options.companyId
            }"
          />
        </v-dialog>
        <v-dialog v-model="isOpenEditing" width="50vw">
          <editing-form
            v-if="isOpenEditing"
            :item-id="activeId"
            @cancel="closeEditing"
            @error="failed"
            @success="updated"
          />
        </v-dialog>

        <v-dialog v-model="isOpenInvoiceCreation" fullscreen>
          <invoice-creation-form
            v-if="isOpenInvoiceCreation"
            :payment-id="activePaymentId"
            :company-id="activeCompanyId"
            @cancel="closeInvoiceCreation"
            @error="failedInvoiceCreation"
            @success="successInvoiceCreation"
          />
        </v-dialog>

        <v-confirmation
          v-model="isOpenCharge"
          :title="$lang('companies.payments.charge')"
          :message="$lang('companies.payments.charge_confirmation')"
          :options="{
            loading: chargeRequest.loading && chargeRequest.params.id === activeId,
            yes: confirmCharge
          }"
        />

      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
  import { mapState } from "vuex";

  import {
    GET_COMPANY_PAYMENTS_PAGINATION,
    REMOVE_COMPANY_PAYMENTS,
    CHARGE_COMPANY_PAYMENTS,
  } from "@/store/company-payments/actions";

  import {
    EXPORT_XML_COMPANY_INVOICES,
  } from "@/store/company-invoices/actions";

  import EditingForm from "@/pages/company-payments/EditingForm";
  import CreationForm from "@/pages/company-payments/CreationForm";
  import InvoiceCreationForm from "@/pages/company-invoices/CreationForm";
  import OnboardingStatus from '@/pages/companies/components/OnboardingStatus.vue';

  import crud from "@/mixins/crud";

  export default {
    name: "CompanyPaymentsList",
    mixins: [
      crud,
    ],
    components: {
      EditingForm,
      CreationForm,
      InvoiceCreationForm,
      OnboardingStatus,
    },
    props: {
      withCompany: {
        type: Boolean,
        default: true,
      },
      withFilters: {
        type: Boolean,
        default: true,
      },
      options: {
        type: Object,
        required: false,
        default: () => {
          return {};
        }
      }
    },
    data() {
      return {
        filters: {
          order: 'asc',
          sortBy: "scheduled_at",
          is_charged: 0,
        },
        activePaymentId: null,
        activeCompanyId: null,
        isOpenInvoiceCreation: false,
        skipStoreFilters: true,
        isOpenCharge: false,
      };
    },
    methods: {
      dataAction() {
        return GET_COMPANY_PAYMENTS_PAGINATION;
      },
      removeAction() {
        return REMOVE_COMPANY_PAYMENTS;
      },
      dataActionParams() {
        let params = {
          _data: {
            relations: {
              invoice_detail: {
                selects: [
                  'id',
                  'company_payment_id',
                  'company_invoice_id',
                ]
              }
            }
          }
        };

        if (this.options.companyId) {
          params.company_id = this.options.companyId;
        }

        if (this.withCompany) {
          params = {
            ...params,
            _data: {
              relations: {
                ...params._data.relations,
                company: {
                  selects: [
                    'id',
                    'name',
                    'onboarding_status',
                  ],
                  relations: {
                    subscription_plan: {
                      selects: [
                        'company_id',
                        'stripe_payment_method_id'
                      ]
                    }
                  }
                }
              }
            }
          }
        }

        return params;
      },
      charge(id) {
        this.activeId = id;
        this.isOpenCharge = true;
      },
      confirmCharge() {
        this.$store.dispatch(CHARGE_COMPANY_PAYMENTS, {
          id: this.activeId
        }).then(() => {
          this.getData();
          this.showNotification(this.$lang("companies.payments.charge_success"), "success");
          this.isOpenCharge = false;
        })
      },
      canCharge(item) {
        if (!this.withCompany) {
          return this.options.canCharge;
        }

        return !!item.company.subscription_plan &&
               !!item.company.subscription_plan.stripe_payment_method_id &&
               !item.is_disputed;
      },
      openInvoiceCreation(item) {
        this.activeCompanyId = item.company_id;
        this.activePaymentId = item.id;
        this.isOpenInvoiceCreation = true;
      },
      closeInvoiceCreation() {
        this.activePaymentId = null;
        this.isOpenInvoiceCreation = false;
      },
      failedInvoiceCreation() {

      },
      successInvoiceCreation() {
        this.getData();
        this.closeInvoiceCreation();
        this.showNotification(this.$lang("main.crud.success_create"), "success");
      },
      exportInvoiceXML(invoiceId) {
        this.$store.dispatch(EXPORT_XML_COMPANY_INVOICES, invoiceId);
      }
    },
    computed: {
      ...mapState({
        request: state => state.companyPayments.getPaginationRequest,
        deletionLoading: state => state.companyPayments.deletionRequest.loading,
        chargeRequest: state => state.companyPayments.chargeRequest,
      })
    },
  };
</script>

<style scoped>
</style>
